/**
 * CORE-BK responsibility 8 — the self-service surface for
 * "the person may ask what is held, and receive it".
 *
 * Deliberately has NO subject-identifier input. The export is scoped to the
 * caller's own verified identity, resolved server-side: `auth.uid()` for an
 * account, and `customer_users` for a Chronicle customer. A box asking "whose
 * data?" would be the beginning of an exfiltration path, so there isn't one.
 *
 * The wording is careful on purpose. This is an OptiBay product capability,
 * not a statement of compliance with any particular data-protection law —
 * LP-4 records that the statutory position is a legal question that remains
 * open. Reporting a protection you have not verified is worse than reporting
 * nothing, and that applies to what the button promises too.
 */
import { useState } from 'react';
import { Download, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';

export type ExportSubjectType = 'account' | 'customer';

interface Props {
  subjectType: ExportSubjectType;
  /**
   * The Supabase client to invoke with. Chronicle runs its own client, so the
   * caller supplies it rather than this component reaching for a global.
   */
  invoke: (body: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>;
}

const COPY: Record<ExportSubjectType, { title: string; description: string; contains: string[] }> = {
  account: {
    title: 'Download my data',
    description:
      'A copy of the information OptiBay holds about you, as a machine-readable JSON file.',
    contains: [
      'Your account and profile details',
      'Your Passport: experience, education, certifications and references you added',
      'Your Toolbox — the tools you own',
      'Where and when you have worked, and the agreements you accepted',
      'Any record of OptiBay support staff accessing your account',
    ],
  },
  customer: {
    title: 'Download my data',
    description:
      'A copy of the information your shops hold about you, as a machine-readable JSON file.',
    contains: [
      'Your contact details at each shop you are linked to',
      'Your vehicles',
      'Your service history and what you asked for',
      'Recommendations made to you, and your messages with the shop',
      'Your communication consents',
    ],
  },
};

export function DownloadMyDataCard({ subjectType, invoke }: Props) {
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();
  const copy = COPY[subjectType];

  const handleDownload = async () => {
    setIsGenerating(true);
    try {
      const { data, error } = await invoke({ subject_type: subjectType });
      if (error || !data) {
        toast({
          title: 'Could not prepare your download',
          description: 'Something went wrong preparing your data. Please try again.',
          variant: 'destructive',
        });
        return;
      }

      // Generated on demand and handed straight to the browser: OptiBay keeps
      // no second copy of the package.
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `optibay-my-data-${subjectType}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      const status = (data as { status?: string }).status;
      toast({
        title: 'Your data has been downloaded',
        description:
          status === 'partial'
            ? 'Some sections could not be included this time — they are listed in the file.'
            : 'Check your downloads folder for the JSON file.',
      });
    } catch {
      toast({
        title: 'Could not prepare your download',
        description: 'Something went wrong preparing your data. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          {copy.title}
        </CardTitle>
        <CardDescription>{copy.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-muted-foreground">
          <p className="mb-2">It includes:</p>
          <ul className="list-disc pl-5 space-y-1">
            {copy.contains.map(line => <li key={line}>{line}</li>)}
          </ul>
        </div>

        {/* Said plainly rather than buried: some things are deliberately not
            in the package, and the person is better served knowing that. */}
        <p className="text-xs text-muted-foreground">
          To protect other people, the file leaves out information that belongs to someone
          else — other people&apos;s contact details, and shops&apos; internal notes and
          operational records.
        </p>

        <Button onClick={handleDownload} disabled={isGenerating}>
          {isGenerating
            ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Preparing your file…</>
            : <><Download className="mr-2 h-4 w-4" />Download my data</>}
        </Button>
      </CardContent>
    </Card>
  );
}

export default DownloadMyDataCard;
